import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Add, CheckCircle, Block } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { batchAPI } from '../../../services/api';
import ConfirmDialog from '../../../components/ConfirmDialog';

const BatchList = () => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialog, setCreateDialog] = useState(false);
  const [formData, setFormData] = useState({ name: '', year: new Date().getFullYear() });
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const res = await batchAPI.getAll();
      setBatches(res.data.data?.batches || []);
    } catch {
      toast.error('Failed to load batches');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim() || !formData.year) {
      toast.error('Batch name and year are required');
      return;
    }
    try {
      setSaving(true);
      await batchAPI.create({ name: formData.name.trim(), year: Number(formData.year) });
      toast.success('Batch created successfully');
      setCreateDialog(false);
      setFormData({ name: '', year: new Date().getFullYear() });
      fetchBatches();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create batch');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (batch) => {
    setConfirmDialog({
      open: true,
      title: batch.isEnabled ? 'Disable Batch' : 'Enable Batch',
      message: batch.isEnabled
        ? `Disabling "${batch.name}" will prevent branch admins from selecting it for new student admissions. Continue?`
        : `Enable "${batch.name}" for new student admissions?`,
      onConfirm: async () => {
        try {
          await batchAPI.toggleStatus(batch._id);
          toast.success(`Batch ${batch.isEnabled ? 'disabled' : 'enabled'} successfully`);
          fetchBatches();
        } catch {
          toast.error('Failed to update batch status');
        }
      },
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Batch Setting
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => setCreateDialog(true)}>
          Add Batch
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Enabled batches are available for branch admins when enrolling new students. Disabled batches can still be used in filters but not for new admissions.
      </Alert>

      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Batch Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Year</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : batches.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No batches found. Create your first batch.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                batches.map((batch) => (
                  <TableRow key={batch._id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{batch.name}</TableCell>
                    <TableCell>{batch.year}</TableCell>
                    <TableCell>
                      <Chip
                        label={batch.isEnabled ? 'Enabled' : 'Disabled'}
                        color={batch.isEnabled ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(batch.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <Tooltip title={batch.isEnabled ? 'Disable' : 'Enable'}>
                        <IconButton
                          color={batch.isEnabled ? 'success' : 'default'}
                          onClick={() => handleToggle(batch)}
                        >
                          {batch.isEnabled ? <CheckCircle /> : <Block />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create New Batch</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Batch Name"
              value={formData.name}
              onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
              fullWidth
              placeholder="e.g., JUNE-2026, JANUARY-2026"
              helperText="Use format: MONTH-YEAR (e.g. JUNE-2026)"
            />
            <TextField
              label="Year"
              type="number"
              value={formData.year}
              onChange={(e) => setFormData((p) => ({ ...p, year: e.target.value }))}
              fullWidth
              inputProps={{ min: 2000, max: 2100 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={() => {
          confirmDialog.onConfirm?.();
          setConfirmDialog((p) => ({ ...p, open: false }));
        }}
        onClose={() => setConfirmDialog((p) => ({ ...p, open: false }))}
      />
    </Box>
  );
};

export default BatchList;
